// Copyright (c) Docugami, Inc. All rights reserved.

import { LimitsContainer } from 'components/common/LimitsContainer';
import { NavigationType, useNavigationService } from "hooks/navigationService";
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { WorkPath, WorkPathType } from 'networking/WorkPathApi';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from 'store';
import { selectIsLimitedOrganization } from 'store/AccountInfoHelpers';
import { getClusterNameById } from 'store/state_helpers';
import { TelemetryEvent, WorkPathViewerItemClickedSchema } from 'telemetry/TelemetryEvents';
import { Telemetry } from 'telemetry/TelemetryManager';
import { CopyWorkPathDialog } from './CopyWorkPathDialog';
import { DeleteWorkPathDialog } from './DeleteWorkPathDialog';
import { RenameWorkPathDialog } from './RenameWorkPathDialog';
import { WorkPathItem, WorkPathOverflowMenuProps } from './WorkPathItem';
import { getIconInfoForWorkPath, isReportRequired, typeToLabel } from './WorkPathsHelper';
import { WorkPathGridView, WorkPathsListView } from './WorkPathsViews';
import { selectWorkPaths } from 'store/work_path/selectors';
import { useGetWorkPathsQuery, selectAllWorkPaths } from 'store/work_path/workPathSlice';

enum Action {
    None = 'None',
    Renaming = 'Renaming',
    Deleting = 'Deleting',
    Copying = 'Copying'
}

interface ActionState {
    id: string,
    action: Action
}

export enum SortWorkPathByField {
    Name = "Name",
    LastModified = "Recents",
    Type = "Type",
    DocSet = "DocSet"
}

const sortByFunctions = new Map<SortWorkPathByField, (a: WorkPath, b: WorkPath, dispatch: any) => number>([
    [SortWorkPathByField.Name, (a, b) => a.name.localeCompare(b.name)],
    [SortWorkPathByField.LastModified, (a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()],
    [SortWorkPathByField.Type, (a, b) => typeToLabel.get(a.workPathType)!.localeCompare(typeToLabel.get(b.workPathType)!)],
    [SortWorkPathByField.DocSet, (a, b, dispatch) => dispatch(getClusterNameById(a.clusterId)).localeCompare(dispatch(getClusterNameById(b.clusterId)))]
]);

export interface SortedState {
    sortedBy: SortWorkPathByField;
    ascending: boolean;
}

export enum WorkPathsViewType {
    List,
    Icon
}

interface WorkPathsViewerProps {
    title?: string;
    coachMark?: JSX.Element;
    iconHeight: number;
    maxItems?: number;
    viewType: WorkPathsViewType;
    docSetId?: string;
    types?: WorkPathType[];
}

/**
 * Presents a set of work paths to the user to allow them to navigate to, modify, or delete an existing work path
 * @param props
 */
export const WorkPathsViewer = (props: WorkPathsViewerProps): JSX.Element => {
    const { isLoading } = useGetWorkPathsQuery();
    const workPaths = useAppSelector(state => selectAllWorkPaths(selectWorkPaths(state, props.docSetId, props.types)));

    const [items, setItems] = useState<WorkPath[]>(workPaths);
    const visibleItems = useMemo(() => items.slice(0, props.maxItems), [items, props.maxItems])
    const [actionState, setActionState] = useState<ActionState | null>(null);
    const [sortedState, setSortedState] = useState<SortedState | null>(null);

    const navigation = useNavigationService();
    const dispatch = useAppDispatch();

    const isLimited = useSelector((state: AppState) => selectIsLimitedOrganization(state));

    useEffect(() => {
        // Default sort items by recency
        sortItemsAndUpdateState(SortWorkPathByField.LastModified, true);
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [workPaths])

    const sortItemsAndUpdateState = useCallback((sortByType: SortWorkPathByField, overrideSortedState: boolean = false) => {
        function sortItems(workPaths: WorkPath[], currentSortedState: SortedState | null, sortByType: SortWorkPathByField): { sortedItems: WorkPath[], sortedState: SortedState } {
            const compareFn = (a: WorkPath, b: WorkPath) => sortByFunctions.get(sortByType)!(a, b, dispatch);
            let modifiedCompareFn = null;
            let ascending = true;
            if (currentSortedState?.sortedBy === sortByType) {
                // The list is already sorted by this type, reverse sort order
                const modifyBy = currentSortedState.ascending ? -1 : 1;
                ascending = !currentSortedState.ascending;
                modifiedCompareFn = (a: WorkPath, b: WorkPath) => compareFn!(a, b) * modifyBy;
            }

            const sortedItems = [...workPaths].sort(modifiedCompareFn ?? compareFn);
            return { sortedItems: sortedItems, sortedState: { sortedBy: sortByType, ascending: ascending } };
        }

        const { sortedItems, sortedState: newSortedState } = sortItems(workPaths, overrideSortedState ? null : sortedState, sortByType);
        setItems(sortedItems);
        setSortedState(newSortedState);
    }, [workPaths, sortedState, dispatch]);

    const actionDialog = useMemo((): JSX.Element | null => {
        if (!actionState) {
            return null;
        }

        const workPath = workPaths?.find(x => x.id === actionState.id);
        if (!workPath) {
            return null;
        }

        switch (actionState.action) {
            case Action.Renaming:
                return <RenameWorkPathDialog workPath={workPath} onComplete={() => setActionState(null)} />

            case Action.Deleting:
                return <DeleteWorkPathDialog workPathId={workPath.id} onComplete={() => setActionState(null)} onCancel={() => setActionState(null)} isVisible={true} />

            case Action.Copying:
                return <CopyWorkPathDialog
                    workPathId={workPath.id}
                    workPathName={workPath.name}
                    workPathType={workPath.workPathType}
                    clusterId={workPath.clusterId}
                    onDismiss={() => setActionState(null)} />

            default:
                return null;
        }

    }, [actionState, workPaths])

    // Telemetry when a workpath viewer item is clicked
    function onItemClicked(workPathId: string) {
        Telemetry.trackEvent<WorkPathViewerItemClickedSchema>({
            eventName: TelemetryEvent.WorkPathViewerItemClicked,
            workPathId: workPathId
        });
    }

    function handleClick(workPathId: string) {
        onItemClicked(workPathId);
        navigation.navToWorkPathPage(NavigationType.PushNewPage, workPathId);
    }

    function getView() {
        const menuProps = (workPathId: string): WorkPathOverflowMenuProps | undefined => {
            const workPath = workPaths.find(x => x.id === workPathId);

            if (!workPath) {
                return;
            }

            return {
                renameEnabled: !actionState,
                deleteEnabled: !actionState,
                copyEnabled: !actionState && isReportRequired(workPath.workPathType),
                onRenameClicked: () => setActionState({ action: Action.Renaming, id: workPathId }),
                onDeleteClicked: () => setActionState({ action: Action.Deleting, id: workPathId }),
                onCopyClicked: () => setActionState({ action: Action.Copying, id: workPathId })
            }
        }

        if (isLoading) {
            return;
        }

        if (workPaths.length === 0) {
            // There are no workpaths
            return props.coachMark;
        }

        let view: JSX.Element;
        switch (props.viewType) {
            case WorkPathsViewType.Icon:
                view = (
                    <WorkPathGridView>
                        {items.map(item => (
                            <LimitsContainer key={item.id} actionName={`view or modify ${item.name}`}>
                                <WorkPathItem
                                    key={item.id}
                                    title={item.name}
                                    clickHandler={() => handleClick(item.id)}
                                    iconProps={getIconInfoForWorkPath(item.workPathType)}
                                    menuProps={menuProps(item.id)}
                                    iconHeight={props.iconHeight} />
                            </LimitsContainer>
                        ))}
                    </WorkPathGridView>
                );
                break;
            case WorkPathsViewType.List:
                const links = {
                    name: (item: WorkPath) => navigation.getWorkPathPageUrl(item.id) ?? ""
                };
                view = (
                    <WorkPathsListView
                        items={visibleItems}
                        onSelect={(workPathId) => onItemClicked(workPathId)}
                        isItemLimited={() => !!isLimited}
                        menuProps={menuProps}
                        links={links}
                        validateActions={{}}
                        sortItems={sortItemsAndUpdateState}
                        sortedState={sortedState ?? undefined} />
                );
                break;
        }

        return view;
    }

    return (
        <>
            {actionDialog}
            {getView()}
        </>
    );
}
